/**
 * Amazon Mobile Add-to-Basket Popup Controller
 */
class AmazonMobileCartPopup {
  constructor() {
    this.timer = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.popup = document.getElementById('amazon-cart-popup');
    this.image = document.getElementById('amazon-cart-popup-image');
    this.qty = document.getElementById('amazon-cart-popup-qty');
    this.doneBtn = document.getElementById('amazon-cart-popup-done');
    this.backdrop = document.getElementById('amazon-cart-popup-backdrop');

    if (this.doneBtn) {
      this.doneBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hide();
      });
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', (e) => {
        e.preventDefault();
        this.hide();
      });
    }
  }

  show(itemData = {}) {
    // Only operate on mobile devices (width <= 767px)
    if (window.innerWidth > 767) return;

    if (!this.popup || !this.doneBtn) {
      this.bindEvents();
    }

    if (!this.popup) return;

    // Clear any existing timer
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    // Set Quantity
    //
    // Client: adding the same frequently-bought-together item twice kept
    // saying "1", while the main product counts up - 1, then 2 - so you can
    // see how many are now in the basket.
    //
    // The two callers hand in different shapes. The main product form posts a
    // single item and gets the resulting line back, so quantity sits at the
    // top level. The FBT cards post {items:[...]} (fbt-cart-stepper.js) and
    // Shopify answers with {items:[...]} - no top-level quantity at all, so
    // this fell through to the DOM fallback below and read the main product's
    // quantity box, which is 1 whatever the card did. items[0].quantity is
    // the line's running total after the add (verified: 1 then 2 on a repeat
    // add), which is exactly the number the client wants shown. The image
    // block further down already reads items[0] this way.
    let quantity = 1;
    if (itemData.quantity !== undefined && itemData.quantity !== null) {
      quantity = itemData.quantity;
    } else if (itemData.qty !== undefined && itemData.qty !== null) {
      quantity = itemData.qty;
    } else if (itemData.items && itemData.items[0] && itemData.items[0].quantity !== undefined && itemData.items[0].quantity !== null) {
      quantity = itemData.items[0].quantity;
    } else {
      const qtyInput = document.querySelector('form[action*="/cart/add"] [name="quantity"], input.quantity__input');
      if (qtyInput && qtyInput.value) {
        quantity = parseInt(qtyInput.value, 10) || 1;
      }
    }
    if (this.qty) {
      this.qty.textContent = quantity;
    }

    // Set Product Image
    let imageUrl = '';
    if (itemData.image) {
      imageUrl = typeof itemData.image === 'string' ? itemData.image : (itemData.image.url || itemData.image.src || '');
    } else if (itemData.featured_image) {
      imageUrl = typeof itemData.featured_image === 'string' ? itemData.featured_image : (itemData.featured_image.url || itemData.featured_image.src || '');
    } else if (itemData.items && itemData.items[0]) {
      const firstItem = itemData.items[0];
      // The last resort here used to be `|| firstItem.featured_image`, which
      // is an OBJECT on a cart line ({alt, aspect_ratio, height, url, width}).
      // On a line whose featured_image.url is null - which it is on several of
      // these products - that object was assigned straight into imageUrl and
      // the .startsWith() below threw, taking the whole popup down on every
      // frequently-bought-together add. Only ever accept a string.
      imageUrl =
        (typeof firstItem.image === 'string' ? firstItem.image : firstItem.image?.url) ||
        firstItem.featured_image?.url ||
        (typeof firstItem.featured_image === 'string' ? firstItem.featured_image : '') ||
        '';
    }

    if (typeof imageUrl !== 'string') imageUrl = '';

    if (!imageUrl) {
      const mainMediaImg = document.querySelector('.product__media img, .product-single__photo img, .product__main-image img, img.product-featured-media');
      if (mainMediaImg && mainMediaImg.src) {
        imageUrl = mainMediaImg.src;
      }
    }

    // Client: even after the preload-before-reveal fix below stopped the
    // popup ever showing the WRONG colour, it still sometimes took a beat
    // to show the right one. Cause: the cart API's own image URL
    // (cdn.shopify.com/s/files/...) is on a different domain to the one
    // this theme's gallery renders on the page (technomate.com/cdn/shop/
    // files/...) - two different browser cache entries for the same
    // photo, so warming one (the gallery, via colour swatch selection /
    // tmPreloadColourSwatchImages in custom.js) never warms the other, and
    // the popup paid a fresh network fetch regardless of how recently that
    // exact colour had already been looked at on screen.
    //
    // The gallery's own <img> is that already-loaded, already-decoded
    // photo - reading it costs nothing - but it's only the ADDED item's
    // photo when the main product form's own variant matches what was
    // just added. A frequently-bought-together card posts a *different*
    // product/variant through this same show() (fbt-cart-stepper.js /
    // cart-drawer.js's renderContents), while the main gallery keeps
    // showing whatever the shopper is currently browsing - trusting the
    // gallery there would show the wrong product entirely. Only take this
    // fast path when the variant id being added is confirmed to be the one
    // the gallery is currently displaying.
    //
    // No race against an in-progress colour swap either: JS is
    // single-threaded, and the colour-swap handler (custom.js, the block
    // ending in tmRestoreNativeSlideImage/releaseHeldImage) runs to
    // completion synchronously before the browser can dispatch the next
    // click - so by the time an Add to Basket click is even handled, that
    // block has already finished and .slick-current already points at the
    // correct slide.
    const addedVariantId =
      itemData.variant_id ??
      itemData.id ??
      (itemData.items && itemData.items[0] && (itemData.items[0].variant_id ?? itemData.items[0].id));
    const mainFormVariantInput = document.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
    const mainFormVariantId = mainFormVariantInput ? parseInt(mainFormVariantInput.value, 10) : NaN;

    if (addedVariantId !== undefined && addedVariantId !== null && Number(addedVariantId) === mainFormVariantId) {
      const onScreenGalleryImg = document.querySelector('.tm_product-main-img-slide .slick-current img');
      const onScreenSrc = onScreenGalleryImg && (onScreenGalleryImg.currentSrc || onScreenGalleryImg.src);
      if (onScreenSrc) imageUrl = onScreenSrc;
    }

    if (imageUrl) {
      if (imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;
      if (this.image) {
        this.image.alt = itemData.title || itemData.product_title || 'Product photo';
        if (this.image.parentElement) {
          this.image.parentElement.style.display = 'flex';
        }

        // Client: switching colour and immediately adding to basket showed
        // the popup thumbnail as the PREVIOUS colour for a beat before it
        // corrected itself. The <img> element is reused across every add,
        // and a browser keeps painting its old pixels the instant .src
        // changes right up until the new photo has actually loaded - so
        // whichever colour happened to be loaded/cached already is what
        // flashed. Preload the new photo off-screen and only swap the
        // visible src in once it's ready, so the popup only ever shows the
        // colour that was actually added. Skipped when the URL hasn't
        // changed (repeat add of the same variant) since there's nothing to
        // wait on there.
        if (this.image.src !== imageUrl) {
          this.image.style.visibility = 'hidden';
          const preload = new Image();
          const reveal = () => {
            if (this.image) {
              this.image.src = imageUrl;
              this.image.style.visibility = 'visible';
            }
          };
          preload.onload = reveal;
          preload.onerror = reveal;
          preload.src = imageUrl;
        }
      }
    } else if (this.image && this.image.parentElement) {
      this.image.parentElement.style.display = 'none';
    }

    // Show popup & backdrop
    // The snippet sets style="display:none" inline so the popup can never
    // flash visible (unstyled, in normal document flow) during the brief
    // window before amazon-cart-popup.css - loaded non-blocking via a
    // media="print" swap - actually applies its own default `display:none`.
    // Clearing that inline override here hands control back to the
    // (long-since-loaded, by the time a shopper has actually triggered
    // this) CSS, which is what the .active class below needs to take
    // effect at all.
    this.popup.style.display = '';
    this.popup.classList.add('active');
    this.popup.setAttribute('aria-hidden', 'false');

    // Automatically close in 1.25 seconds - client asked for a quarter of a
    // second longer than the original 1000ms so there is more time to read it.
    this.timer = setTimeout(() => {
      this.hide();
    }, 1250);
  }

  hide() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.popup) {
      this.popup.classList.remove('active');
      this.popup.setAttribute('aria-hidden', 'true');
    }
  }
}

if (!window.amazonMobileCartPopup) {
  window.amazonMobileCartPopup = new AmazonMobileCartPopup();
}
